import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import axios from 'axios';
import { UserContext } from '../Context/UserContext';
import {
    Box, Typography, Grid, Card, CardContent, TextField, Button,
    FormControl, InputLabel, Select, MenuItem, Chip, Avatar, List,
    ListItem, ListItemText, ListItemAvatar, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Table, TableHead,
    TableBody, TableRow, TableCell, TableContainer, Paper, Tabs, Tab,
    Divider, Alert, CircularProgress, Collapse, Tooltip, InputAdornment,
    FormControlLabel, Switch, FormHelperText, Autocomplete, LinearProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Receipt as ReceiptIcon,
    Download as DownloadIcon,
    UploadFile as UploadFileIcon,
    PersonAdd as PersonAddIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Tune as TuneIcon,
    Science as ScienceIcon,
    VpnKey as VpnKeyIcon,
    SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const LICENCE_TYPES = [
    { value: 'gratuite',  label: 'Gratuite' },
    { value: 'payante',   label: 'Payante' },
    { value: 'essai',     label: 'Essai' },
    { value: 'recharge',  label: 'Recharge' },
];

// Prix d'un pack (pricing_plan) selon la récurrence de paiement de la licence
const packAmount = (pack, recurrence) => {
    const amount = recurrence === 'annuelle' ? pack.annual_amount : pack.base_amount;
    return amount != null ? String(amount) : '';
};

const RECURRENCES = [
    { value: '',          label: 'Aucune (ponctuel)' },
    { value: 'mensuelle', label: 'Mensuelle' },
    { value: 'annuelle',  label: 'Annuelle' },
    { value: 'unique',    label: 'Unique' },
];

const STATUTS_FACTURE = [
    { value: 'en_attente', label: 'En attente' },
    { value: 'payée',      label: 'Payée' },
    { value: 'impayée',    label: 'Impayée' },
];

const STATUT_COLORS = { payée: 'success', impayée: 'error', en_attente: 'warning' };

export default function ClientManagementPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canConnectorAccess = isSuperAdmin || userPermissions.includes('clients.connector-access');
    const canStartSupport = isSuperAdmin || userPermissions.includes('support.start_session');
    const canManageUsers = isSuperAdmin || userPermissions.includes('clients.users.manage');
    const canManageLicenses = isSuperAdmin || userPermissions.includes('clients.licenses.manage');

    const [client, setClient]           = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [tabValue, setTabValue]       = useState(0);
    const [isEditing, setIsEditing]     = useState(false);
    const [saving, setSaving]           = useState(false);
    const [saveError, setSaveError]     = useState(null);

    // Edit form
    const [editForm, setEditForm] = useState({ nom: '', raison_sociale: '', type_client: 'company' });

    // Subscription (licence) dialog
    const [openSubDialog, setOpenSubDialog] = useState(false);
    const [editingSub, setEditingSub]       = useState(null);
    const [subForm, setSubForm] = useState({
        key_application: '',
        date_debut:      '',
        date_fin:        '',
        licence_type:    'payante',
        montant:         '',
        recurrence:      '',
        pricing_plan_id: '',
        quantity_limit:  '',
        dimension:       '',
        is_beta_tester:  false,
        application_url: '',
        authorizations:  [],
    });
    const [subSaving, setSubSaving] = useState(false);
    const [subError, setSubError]   = useState(null);

    // Packs (pricing_plan avec un quota mensuel) proposés pour chaque application
    const [packsCatalog, setPacksCatalog] = useState({}); // { key_application: [...] }

    const loadPacksCatalog = (keyApplication) => {
        if (!keyApplication || packsCatalog[keyApplication]) return;
        const token = getToken();
        if (!token) return;
        axios.get(`${API_URL}/api/pricing-plans`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { priceable_type: 'application', priceable_id: keyApplication }
        })
            .then(r => setPacksCatalog(p => ({
                ...p,
                [keyApplication]: (Array.isArray(r.data) ? r.data : []).filter(plan => plan.quantity_limit != null && plan.is_active)
            })))
            .catch(err => console.error('Erreur chargement packs:', err.message));
    };

    // Quota du cycle en cours (accordé, consommé, reste) par application, et recharge ponctuelle
    const [quotas, setQuotas] = useState({}); // { key_application: { period_start, period_end, quotas: [...] } }
    const [topupTarget, setTopupTarget] = useState(null); // { key_application, dimension }
    const [topupForm, setTopupForm]     = useState({ quantity: '', montant: '' });
    const [topupSaving, setTopupSaving] = useState(false);
    const [topupError, setTopupError]   = useState(null);

    const loadQuota = (keyApplication) => {
        const token = getToken();
        if (!token || !keyApplication) return;
        axios.get(`${API_URL}/api/clients/${id}/applications/${encodeURIComponent(keyApplication)}/quota`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => setQuotas(p => ({ ...p, [keyApplication]: r.data })))
            .catch(err => console.error('Erreur chargement quota:', err.message));
    };

    // Rechargé à chaque changement de licence : un nouveau pack ou une recharge modifie le quota
    const quotaApplications = [...new Set((client?.ClientApplications || [])
        .filter(ca => ca.quantity_limit != null).map(ca => ca.key_application))].join('|');
    useEffect(() => {
        const keys = quotaApplications.split('|').filter(Boolean);
        setQuotas(p => Object.fromEntries(Object.entries(p).filter(([key]) => keys.includes(key))));
        keys.forEach(loadQuota);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotaApplications, client]);

    const openTopupDialog = (keyApplication, dimension) => {
        setTopupTarget({ key_application: keyApplication, dimension });
        setTopupForm({ quantity: '', montant: '' });
        setTopupError(null);
    };

    const handleSaveTopup = () => {
        const token = getToken();
        if (!token || !topupTarget) return;
        setTopupSaving(true);
        setTopupError(null);
        const headers = { Authorization: `Bearer ${token}` };
        axios.post(`${API_URL}/api/clients/${id}/applications/${encodeURIComponent(topupTarget.key_application)}/topups`, {
            quantity:  parseFloat(topupForm.quantity),
            dimension: topupTarget.dimension,
            montant:   topupForm.montant ? parseFloat(topupForm.montant) : null,
        }, { headers })
            .then(() => axios.get(`${API_URL}/api/clients/${id}`, { headers }))
            .then(r => { setClient(r.data); setTopupTarget(null); })
            .catch(err => setTopupError(err.response?.data?.error || err.message))
            .finally(() => setTopupSaving(false));
    };

    // Choix d'un pack dans le dialogue de licence : son quota, son prix (selon la récurrence) et
    // ses droits pré-remplissent le formulaire, qui reste modifiable. '' = quantité personnalisée.
    const handleSelectPack = (planId) => {
        const pack = (packsCatalog[subForm.key_application] || []).find(p => String(p.id) === String(planId));
        if (!pack) {
            setSubForm(p => ({ ...p, pricing_plan_id: '' }));
            return;
        }
        setSubForm(p => ({
            ...p,
            pricing_plan_id: String(pack.id),
            quantity_limit:  String(parseFloat(pack.quantity_limit)),
            dimension:       pack.dimension || p.dimension,
            montant:         p.licence_type === 'gratuite' ? p.montant : packAmount(pack, p.recurrence),
            authorizations:  Array.isArray(pack.authorizations) && pack.authorizations.length > 0 ? pack.authorizations : p.authorizations,
        }));
    };

    const handleRecurrenceChange = (recurrence) => {
        const pack = (packsCatalog[subForm.key_application] || []).find(p => String(p.id) === String(subForm.pricing_plan_id));
        setSubForm(p => ({ ...p, recurrence, montant: pack ? packAmount(pack, recurrence) : p.montant }));
    };

    // Catalogue des permissions (ApplicationPermission) déclarées sur chaque application, pour
    // restreindre les sélecteurs "Droits accordés" aux clés réellement définies (évite les fautes
    // de frappe qui rendaient une authorization silencieusement inopérante).
    const [permissionsCatalog, setPermissionsCatalog] = useState({}); // { key_application: [...] }

    const loadPermissionsCatalog = (keyApplication) => {
        if (!keyApplication || permissionsCatalog[keyApplication]) return;
        const token = getToken();
        if (!token) return;
        axios.get(`${API_URL}/api/permissions`, { headers: { Authorization: `Bearer ${token}` }, params: { application_id: keyApplication } })
            .then(r => setPermissionsCatalog(p => ({ ...p, [keyApplication]: Array.isArray(r.data) ? r.data : [] })))
            .catch(err => console.error('Erreur chargement catalogue permissions:', err.message));
    };

    // Licences d'options (indépendantes de la licence de l'application)
    const [expandedOptionsFor, setExpandedOptionsFor] = useState(null);
    const [optionsCatalog, setOptionsCatalog]         = useState({}); // { key_application: [...] } (catalogue application_manager)
    const [subOptions, setSubOptions]                 = useState({}); // { key_application: [...] } (licences du client)
    const [subOptionsLoading, setSubOptionsLoading]   = useState({});
    const [openOptionLicenseDialog, setOpenOptionLicenseDialog] = useState(false);
    const [editingOptionLicense, setEditingOptionLicense]       = useState(null);
    const [optionLicenseForApp, setOptionLicenseForApp]         = useState(null);
    const [optionLicenseForm, setOptionLicenseForm] = useState({
        application_option_id: '', date_debut: '', date_fin: '', licence_type: 'payante',
        montant: '', recurrence: '', quantity_limit: '', dimension: '', is_beta_tester: false,
        authorizations: [],
    });
    const [optionLicenseSaving, setOptionLicenseSaving] = useState(false);
    const [optionLicenseError, setOptionLicenseError]   = useState(null);

    // Utilisateurs
    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [userForm, setUserForm] = useState({ nom: '', prenom: '', email: '', password: '' });
    const [userPasswordMode, setUserPasswordMode] = useState('temp'); // 'temp' | 'manual'
    const [userShowPassword, setUserShowPassword] = useState(false);
    const [userSaving, setUserSaving]   = useState(false);
    const [userError, setUserError]     = useState(null);

    // Accès Connector avec consentement (5 min / utilisateur cible)
    const [connectorTargetUser, setConnectorTargetUser] = useState(null); // ouvre le dialogue si non-null
    const [connectorRequestId, setConnectorRequestId]   = useState(null);
    const [connectorStep, setConnectorStep]             = useState('waiting'); // waiting | code_entry | active | error
    const [connectorCode, setConnectorCode]             = useState('');
    const [connectorError, setConnectorError]           = useState(null);
    const [connectorSubmitting, setConnectorSubmitting] = useState(false);

    // ── Session de support live ──────────────────────────────────────────────
    const [supportTargetUser, setSupportTargetUser]     = useState(null); // ouvre le dialogue si non-null
    const [supportStep, setSupportStep]                 = useState('starting'); // starting | needs_link | otp_entry | active | error
    const [supportSessionToken, setSupportSessionToken] = useState(null);
    // Token d'impersonation co-navigation (voir plan steady-watching-leaf.md) -- transite
    // uniquement en mémoire (state React + navigation state), jamais dans une URL/l'historique,
    // contrairement à supportSessionToken.
    const [supportAccessToken, setSupportAccessToken]   = useState(null);
    const [supportOtp, setSupportOtp]                   = useState('');
    const [supportError, setSupportError]               = useState(null);
    const [supportSubmitting, setSupportSubmitting]     = useState(false);
    const [supportClientOnline, setSupportClientOnline] = useState(true);
    const [supportAccountKeyInput, setSupportAccountKeyInput] = useState('');

    // Factures
    const [factures, setFactures]           = useState({});     // { key_application: [...] }
    const [facturesLoading, setFacturesLoading] = useState({});
    const [expandedSub, setExpandedSub]     = useState(null);
    const [openFactureDialog, setOpenFactureDialog] = useState(false);
    const [factureUploadSub, setFactureUploadSub]   = useState(null);
    const [factureFile, setFactureFile]     = useState(null);
    const [factureForm, setFactureForm] = useState({ date_facture: '', montant: '', statut: 'en_attente' });
    const [factureUploading, setFactureUploading] = useState(false);
    const [factureError, setFactureError]   = useState(null);

    const getToken = () => document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];

    useEffect(() => {
        const token = getToken();
        if (!token || !id) return;
        const headers = { Authorization: `Bearer ${token}` };
        setLoading(true);
        Promise.all([
            axios.get(`${API_URL}/api/clients/${id}`, { headers }),
            axios.get(`${API_URL}/api/applications`, { headers }),
        ])
            .then(([clientRes, appsRes]) => {
                const c = clientRes.data;
                setClient(c);
                setEditForm({ nom: c.nom || '', raison_sociale: c.raison_sociale || '', type_client: c.type_client || 'company' });
                setApplications(Array.isArray(appsRes.data) ? appsRes.data : []);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [id]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const displayName = client
        ? (client.type_client === 'company' ? (client.raison_sociale || client.nom || '—') : (client.nom || '—'))
        : '—';

    const getAppName = (keyApplication) =>
        applications.find(a => a.client_id === keyApplication)?.name || keyApplication;

    const isSubActive = (sub) => !sub.date_fin || new Date(sub.date_fin) >= new Date();

    const formatMontant = (sub) => {
        if (sub.licence_type === 'gratuite') return 'Gratuite';
        if (sub.montant == null) return '—';
        const r = sub.recurrence ? ` / ${sub.recurrence.replace('mensuelle','mois').replace('annuelle','an')}` : '';
        return `${parseFloat(sub.montant).toFixed(2)} €${r}`;
    };

    // ── Client save ───────────────────────────────────────────────────────────
    const handleSave = () => {
        const token = getToken();
        if (!token) return;
        setSaving(true);
        setSaveError(null);
        axios.put(`${API_URL}/api/clients/${id}`, editForm, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => { setClient(prev => ({ ...prev, ...res.data })); setIsEditing(false); })
            .catch(err => setSaveError(err.response?.data?.error || err.message))
            .finally(() => setSaving(false));
    };

    // ── Utilisateurs ──────────────────────────────────────────────────────────
    const openAddUserDialog = () => {
        setUserForm({ nom: '', prenom: '', email: '', password: '' });
        setUserPasswordMode('temp');
        setUserShowPassword(false);
        setUserError(null);
        setOpenUserDialog(true);
    };

    const handleAddUser = () => {
        const token = getToken();
        if (!token || !userForm.nom || !userForm.prenom || !userForm.email) return;
        if (userPasswordMode === 'manual' && !userForm.password) return;
        setUserSaving(true);
        setUserError(null);
        const payload = {
            nom: userForm.nom,
            prenom: userForm.prenom,
            email: userForm.email,
            ...(userPasswordMode === 'manual' ? { password: userForm.password } : {}),
        };
        axios.post(`${API_URL}/api/clients/${id}/utilisateurs`, payload, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => axios.get(`${API_URL}/api/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } }))
            .then(r => { setClient(r.data); setOpenUserDialog(false); })
            .catch(err => setUserError(err.response?.data?.error || err.message))
            .finally(() => setUserSaving(false));
    };

    const handleDeleteUser = (utilisateurId) => {
        const token = getToken();
        if (!token) return;
        axios.delete(`${API_URL}/api/clients/${id}/utilisateurs/${utilisateurId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => setClient(prev => ({
                ...prev,
                Utilisateurs: (prev.Utilisateurs || []).filter(u => u.id_utilisateur !== utilisateurId),
            })))
            .catch(err => console.error('Erreur suppression utilisateur:', err.message));
    };

    // ── Accès Connector avec consentement ───────────────────────────────────────
    const startConnectorAccess = (u) => {
        const token = getToken();
        if (!token || !u.public_id) return;
        setConnectorTargetUser(u);
        setConnectorRequestId(null);
        setConnectorStep('waiting');
        setConnectorCode('');
        setConnectorError(null);
        axios.post(`${API_URL}/api/clients/${id}/connector-access/request`,
            { target_user_key: u.public_id },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then(r => setConnectorRequestId(r.data.id))
            .catch(err => {
                const code = err.response?.status;
                if (code === 410) setConnectorError("Cet utilisateur n'est pas connecté à Connector actuellement.");
                else if (code === 409) setConnectorError("Une demande est déjà en cours pour ce compte.");
                else setConnectorError(err.response?.data?.error || err.message);
                setConnectorStep('error');
            });
    };

    // Poll le statut tant qu'on attend la réponse de l'utilisateur.
    useEffect(() => {
        if (connectorStep !== 'waiting' || !connectorRequestId) return;
        const token = getToken();
        const interval = setInterval(() => {
            axios.get(`${API_URL}/api/clients/${id}/connector-access/${connectorRequestId}/status`,
                { headers: { Authorization: `Bearer ${token}` } }
            ).then(r => {
                if (r.data.status === 'approved') setConnectorStep('code_entry');
                else if (r.data.status === 'denied') { setConnectorError("L'utilisateur a refusé la demande."); setConnectorStep('error'); }
                else if (r.data.status === 'expired') { setConnectorError('La demande a expiré sans réponse.'); setConnectorStep('error'); }
            }).catch(() => {});
        }, 2000);
        return () => clearInterval(interval);
    }, [connectorStep, connectorRequestId, id]);

    const submitConnectorCode = () => {
        const token = getToken();
        if (!token || !connectorCode.trim()) return;
        setConnectorSubmitting(true);
        setConnectorError(null);
        axios.post(`${API_URL}/api/clients/${id}/connector-access/${connectorRequestId}/redeem`,
            { code: connectorCode.trim() },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then(() => setConnectorStep('active'))
            .catch(err => {
                const code = err.response?.data?.error;
                setConnectorError(code === 'invalid_code' ? 'Code incorrect.' : (code === 'too_many_attempts' ? 'Trop de tentatives, demande expirée.' : (err.response?.data?.error || err.message)));
            })
            .finally(() => setConnectorSubmitting(false));
    };

    const closeConnectorDialog = () => {
        setConnectorTargetUser(null);
        setConnectorRequestId(null);
        setConnectorStep('waiting');
        setConnectorCode('');
        setConnectorError(null);
    };

    // ── Session de support live avec handshake OTP ──────────────────────────────
    // Contrairement à l'accès Connector, il n'y a pas d'étape d'approbation côté client à
    // attendre : l'OTP est poussé immédiatement, le client le dicte par téléphone, l'admin le
    // saisit ici pour autoriser l'accès (voir routes/supportSessionRoutes.js côté qrcode_server).
    const startSupportSession = (u) => {
        const token = getToken();
        if (!token || !u.public_id) return;
        setSupportTargetUser(u);
        setSupportStep('starting');
        setSupportSessionToken(null);
        setSupportOtp('');
        setSupportError(null);
        axios.post(`${API_URL}/api/clients/${id}/support/start`,
            { target_user_key: u.public_id },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then(r => {
                setSupportSessionToken(r.data.sessionToken);
                setSupportClientOnline(r.data.clientOnline);
                setSupportStep('otp_entry');
            })
            .catch(err => {
                if (err.response?.status === 404) setSupportStep('needs_link');
                else { setSupportError(err.response?.data?.error || err.message); setSupportStep('error'); }
            });
    };

    const submitSupportLink = () => {
        const token = getToken();
        if (!token || !supportAccountKeyInput.trim()) return;
        setSupportSubmitting(true);
        setSupportError(null);
        axios.post(`${API_URL}/api/clients/${id}/support/link`,
            { accountKey: supportAccountKeyInput.trim() },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then(() => startSupportSession(supportTargetUser))
            .catch(err => { setSupportError(err.response?.data?.error || err.message); setSupportStep('error'); })
            .finally(() => setSupportSubmitting(false));
    };

    const submitSupportOtp = () => {
        const token = getToken();
        if (!token || !supportOtp.trim()) return;
        setSupportSubmitting(true);
        setSupportError(null);
        axios.post(`${API_URL}/api/clients/${id}/support/${supportSessionToken}/validate-otp`,
            { otp: supportOtp.trim() },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then((res) => { setSupportAccessToken(res.data.accessToken); setSupportStep('active'); })
            .catch(err => {
                const reason = err.response?.data?.reason;
                const messages = {
                    wrong_otp: 'Code incorrect.',
                    too_many_attempts: 'Trop de tentatives, la session a été révoquée.',
                    expired: 'Le code a expiré, relancez une demande.',
                };
                setSupportError(messages[reason] || err.response?.data?.error || err.message);
            })
            .finally(() => setSupportSubmitting(false));
    };

    const closeSupportDialog = () => {
        setSupportTargetUser(null);
        setSupportSessionToken(null);
        setSupportAccessToken(null);
        setSupportOtp('');
        setSupportError(null);
        setSupportStep('starting');
    };

    const openSupportViewer = () => {
        navigate(`/clients/${id}/support/${supportSessionToken}`, { state: { accessToken: supportAccessToken } });
        closeSupportDialog();
    };

    // ── Subscriptions ─────────────────────────────────────────────────────────
    const openAddSubDialog = () => {
        setEditingSub(null);
        setSubForm({
            key_application: '', date_debut: '', date_fin: '', licence_type: 'payante',
            montant: '', recurrence: '', pricing_plan_id: '', quantity_limit: '', dimension: '', is_beta_tester: false,
            application_url: '', authorizations: [],
        });
        setSubError(null);
        setOpenSubDialog(true);
    };

    const openEditSubDialog = (sub) => {
        setEditingSub(sub);
        loadPermissionsCatalog(sub.key_application);
        loadPacksCatalog(sub.key_application);
        setSubForm({
            key_application: sub.key_application,
            date_debut:      sub.date_debut   || '',
            date_fin:        sub.date_fin     || '',
            licence_type:    sub.licence_type || 'payante',
            montant:         sub.montant != null ? String(sub.montant) : '',
            recurrence:      sub.recurrence   || '',
            pricing_plan_id: sub.pricing_plan_id != null ? String(sub.pricing_plan_id) : '',
            quantity_limit:  sub.quantity_limit != null ? String(sub.quantity_limit) : '',
            dimension:       sub.dimension || '',
            is_beta_tester:  !!sub.is_beta_tester,
            application_url: sub.application_url || '',
            authorizations:  sub.authorizations || [],
        });
        setSubError(null);
        setOpenSubDialog(true);
    };

    // Une nouvelle licence (POST) crée toujours une ligne à part — un client peut cumuler
    // plusieurs licences actives pour la même application (ex: gratuite + payante + boost).
    // L'édition (PUT) cible une licence précise par son id, sans jamais toucher les autres.
    const handleSaveSub = () => {
        const token = getToken();
        if (!token || !subForm.key_application) return;
        setSubSaving(true);
        setSubError(null);
        const payload = {
            key_application: subForm.key_application,
            date_debut:      subForm.date_debut   || null,
            date_fin:        subForm.date_fin     || null,
            licence_type:    subForm.licence_type || 'payante',
            montant:         subForm.montant ? parseFloat(subForm.montant) : null,
            recurrence:      subForm.recurrence   || null,
            // Pack choisi (le serveur en reprend le nom) ou quantité personnalisée (aucun pack)
            pricing_plan_id:   subForm.pricing_plan_id ? parseInt(subForm.pricing_plan_id, 10) : null,
            pricing_plan_name: null,
            quantity_limit:  subForm.quantity_limit !== '' ? parseFloat(subForm.quantity_limit) : null,
            dimension:       subForm.dimension    || null,
            is_beta_tester:  subForm.is_beta_tester,
            application_url: subForm.application_url || null,
            authorizations:  subForm.authorizations,
        };
        const req = editingSub
            ? axios.put(`${API_URL}/api/clients/${id}/applications/${editingSub.id}`, payload, { headers: { Authorization: `Bearer ${token}` } })
            : axios.post(`${API_URL}/api/clients/${id}/applications`, payload, { headers: { Authorization: `Bearer ${token}` } });
        req
            .then(() => axios.get(`${API_URL}/api/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } }))
            .then(r => { setClient(r.data); setOpenSubDialog(false); })
            .catch(err => setSubError(err.response?.data?.error || err.message))
            .finally(() => setSubSaving(false));
    };

    const handleDeleteSub = (sub) => {
        const token = getToken();
        if (!token) return;
        axios.delete(`${API_URL}/api/clients/${id}/applications/${sub.id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => setClient(prev => ({
                ...prev,
                ClientApplications: (prev.ClientApplications || []).filter(ca => ca.id !== sub.id),
            })))
            .catch(err => console.error('Error removing subscription:', err.message));
    };

    // ── Licences d'options ───────────────────────────────────────────────────
    const toggleExpandOptions = (keyApplication) => {
        const opening = expandedOptionsFor !== keyApplication;
        setExpandedOptionsFor(opening ? keyApplication : null);
        if (!opening) return;

        const token = getToken();
        if (!token) return;

        if (!optionsCatalog[keyApplication]) {
            axios.get(`${API_URL}/api/options`, { headers: { Authorization: `Bearer ${token}` }, params: { application_id: keyApplication } })
                .then(r => setOptionsCatalog(p => ({ ...p, [keyApplication]: Array.isArray(r.data) ? r.data : [] })))
                .catch(err => console.error('Erreur chargement catalogue options:', err.message));
        }

        setSubOptionsLoading(p => ({ ...p, [keyApplication]: true }));
        axios.get(`${API_URL}/api/clients/${id}/options`, { headers: { Authorization: `Bearer ${token}` }, params: { key_application: keyApplication } })
            .then(r => setSubOptions(p => ({ ...p, [keyApplication]: Array.isArray(r.data) ? r.data : [] })))
            .catch(err => console.error('Erreur chargement licences options:', err.message))
            .finally(() => setSubOptionsLoading(p => ({ ...p, [keyApplication]: false })));
    };

    const getOptionName = (keyApplication, applicationOptionId) =>
        optionsCatalog[keyApplication]?.find(o => o.id === applicationOptionId)?.name || `Option #${applicationOptionId}`;

    const openAddOptionLicense = (keyApplication) => {
        setOptionLicenseForApp(keyApplication);
        loadPermissionsCatalog(keyApplication);
        setEditingOptionLicense(null);
        setOptionLicenseForm({
            application_option_id: '', date_debut: '', date_fin: '', licence_type: 'payante',
            montant: '', recurrence: '', quantity_limit: '', dimension: '', is_beta_tester: false,
            authorizations: [],
        });
        setOptionLicenseError(null);
        setOpenOptionLicenseDialog(true);
    };

    const openEditOptionLicense = (keyApplication, opt) => {
        setOptionLicenseForApp(keyApplication);
        loadPermissionsCatalog(keyApplication);
        setEditingOptionLicense(opt);
        setOptionLicenseForm({
            application_option_id: opt.application_option_id,
            date_debut:             opt.date_debut   || '',
            date_fin:               opt.date_fin     || '',
            licence_type:           opt.licence_type || 'payante',
            montant:                opt.montant != null ? String(opt.montant) : '',
            recurrence:             opt.recurrence   || '',
            quantity_limit:         opt.quantity_limit != null ? String(opt.quantity_limit) : '',
            dimension:              opt.dimension || '',
            is_beta_tester:         !!opt.is_beta_tester,
            authorizations:         opt.authorizations || [],
        });
        setOptionLicenseError(null);
        setOpenOptionLicenseDialog(true);
    };

    const handleSaveOptionLicense = () => {
        const token = getToken();
        if (!token || !optionLicenseForApp || !optionLicenseForm.application_option_id) return;
        setOptionLicenseSaving(true);
        setOptionLicenseError(null);
        axios.post(`${API_URL}/api/clients/${id}/options`, {
            key_application:        optionLicenseForApp,
            application_option_id:  optionLicenseForm.application_option_id,
            date_debut:              optionLicenseForm.date_debut   || null,
            date_fin:                optionLicenseForm.date_fin     || null,
            licence_type:            optionLicenseForm.licence_type || 'payante',
            montant:                 optionLicenseForm.montant ? parseFloat(optionLicenseForm.montant) : null,
            recurrence:              optionLicenseForm.recurrence   || null,
            quantity_limit:          optionLicenseForm.quantity_limit !== '' ? parseFloat(optionLicenseForm.quantity_limit) : null,
            dimension:               optionLicenseForm.dimension    || null,
            is_beta_tester:          optionLicenseForm.is_beta_tester,
            authorizations:          optionLicenseForm.authorizations,
        }, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => axios.get(`${API_URL}/api/clients/${id}/options`, {
                headers: { Authorization: `Bearer ${token}` }, params: { key_application: optionLicenseForApp }
            }))
            .then(r => {
                setSubOptions(p => ({ ...p, [optionLicenseForApp]: Array.isArray(r.data) ? r.data : [] }));
                setOpenOptionLicenseDialog(false);
            })
            .catch(err => setOptionLicenseError(err.response?.data?.error || err.message))
            .finally(() => setOptionLicenseSaving(false));
    };

    const handleDeleteOptionLicense = (keyApplication, applicationOptionId) => {
        const token = getToken();
        if (!token) return;
        axios.delete(`${API_URL}/api/clients/${id}/options/${applicationOptionId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => setSubOptions(p => ({
                ...p,
                [keyApplication]: (p[keyApplication] || []).filter(o => o.application_option_id !== applicationOptionId),
            })))
            .catch(err => console.error('Erreur suppression licence option:', err.message));
    };

    // ── Factures ──────────────────────────────────────────────────────────────
    const loadFactures = (keyApplication) => {
        const token = getToken();
        if (!token) return;
        setFacturesLoading(p => ({ ...p, [keyApplication]: true }));
        axios.get(`${API_URL}/api/clients/${id}/factures?key_application=${keyApplication}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => setFactures(p => ({ ...p, [keyApplication]: r.data })))
            .catch(err => console.error('Erreur factures:', err.message))
            .finally(() => setFacturesLoading(p => ({ ...p, [keyApplication]: false })));
    };

    const toggleExpandSub = (keyApplication) => {
        if (expandedSub === keyApplication) {
            setExpandedSub(null);
        } else {
            setExpandedSub(keyApplication);
            if (!factures[keyApplication]) loadFactures(keyApplication);
        }
    };

    const openUploadDialog = (keyApplication) => {
        setFactureUploadSub(keyApplication);
        setFactureFile(null);
        setFactureForm({ date_facture: '', montant: '', statut: 'en_attente' });
        setFactureError(null);
        setOpenFactureDialog(true);
    };

    const handleUploadFacture = () => {
        const token = getToken();
        if (!token || !factureFile || !factureUploadSub) return;
        setFactureUploading(true);
        setFactureError(null);

        const formData = new FormData();
        formData.append('fichier', factureFile);
        formData.append('key_application', factureUploadSub);
        if (factureForm.date_facture) formData.append('date_facture', factureForm.date_facture);
        if (factureForm.montant)      formData.append('montant', factureForm.montant);
        formData.append('statut', factureForm.statut || 'en_attente');

        axios.post(`${API_URL}/api/clients/${id}/factures`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        })
            .then(r => {
                setFactures(p => ({
                    ...p,
                    [factureUploadSub]: [r.data, ...(p[factureUploadSub] || [])],
                }));
                setOpenFactureDialog(false);
            })
            .catch(err => setFactureError(err.response?.data?.error || err.message))
            .finally(() => setFactureUploading(false));
    };

    const handleDownloadFacture = (factureId, nomOriginal) => {
        const token = getToken();
        if (!token) return;
        axios.get(`${API_URL}/api/clients/${id}/factures/${factureId}/download`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
        })
            .then(r => {
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = nomOriginal || `facture_${factureId}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            })
            .catch(err => console.error('Erreur téléchargement:', err.message));
    };

    const handleDeleteFacture = (keyApplication, factureId) => {
        const token = getToken();
        if (!token) return;
        axios.delete(`${API_URL}/api/clients/${id}/factures/${factureId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => setFactures(p => ({
                ...p,
                [keyApplication]: (p[keyApplication] || []).filter(f => f.id !== factureId),
            })))
            .catch(err => console.error('Erreur suppression facture:', err.message));
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
        </Box>
    );

    if (error || !client) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">{error || 'Client introuvable'}</Alert>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mt: 2 }}>Retour</Button>
        </Box>
    );

    const subscriptions = client.ClientApplications || [];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100vw', overflowX: 'hidden', boxSizing: 'border-box' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: { xs: 2, md: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexGrow: 1 }}>
                    <IconButton onClick={() => navigate('/clients')} sx={{ mr: 2, flexShrink: 0 }}><ArrowBackIcon /></IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip label={client.type_client === 'company' ? 'Entreprise' : 'Particulier'} color="primary" size="small"
                                icon={client.type_client === 'company' ? <BusinessIcon /> : <PersonIcon />} />
                            <Typography variant="body2" color="text.secondary">
                                Depuis le {new Date(client.date_creation).toLocaleDateString('fr-FR')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                        variant={isEditing ? 'contained' : 'outlined'}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : isEditing ? <SaveIcon /> : <EditIcon />}
                        onClick={isEditing ? handleSave : () => { setIsEditing(true); setSaveError(null); }}
                        disabled={saving}
                        sx={{ bgcolor: isEditing ? 'primary.main' : 'transparent', borderColor: 'primary.main', color: isEditing ? 'white' : 'primary.main', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                    >
                        {saving ? 'Sauvegarde...' : isEditing ? 'Sauvegarder' : 'Modifier'}
                    </Button>
                    {isEditing && <Button variant="outlined" onClick={() => { setIsEditing(false); setSaveError(null); }}>Annuler</Button>}
                </Box>
            </Box>

            {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
                    <Tab label="Informations" />
                    <Tab label={`Licences (${subscriptions.length})`} />
                </Tabs>
            </Box>

            {/* ── Tab 0 : Informations ─────────────────────────────────────── */}
            {tabValue === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>Identité</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Nom"
                                            value={isEditing ? editForm.nom : (client.nom || '')}
                                            onChange={(e) => setEditForm(p => ({ ...p, nom: e.target.value }))}
                                            disabled={!isEditing} />
                                    </Grid>
                                    {(isEditing ? editForm.type_client : client.type_client) === 'company' && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth size="small" label="Raison sociale"
                                                value={isEditing ? editForm.raison_sociale : (client.raison_sociale || '')}
                                                onChange={(e) => setEditForm(p => ({ ...p, raison_sociale: e.target.value }))}
                                                disabled={!isEditing} />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small" disabled={!isEditing}>
                                            <InputLabel>Type</InputLabel>
                                            <Select value={isEditing ? editForm.type_client : client.type_client}
                                                onChange={(e) => setEditForm(p => ({ ...p, type_client: e.target.value }))} label="Type">
                                                <MenuItem value="company">Entreprise</MenuItem>
                                                <MenuItem value="individual">Particulier</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Date de création"
                                            value={new Date(client.date_creation).toLocaleDateString('fr-FR')} disabled />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                        Utilisateurs ({client.Utilisateurs?.length ?? 0})
                                    </Typography>
                                    {canManageUsers && (
                                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAddUserDialog}
                                            sx={{ borderColor: 'primary.main', color: 'primary.main', '&:hover': { bgcolor: '#fff3ef' } }}>
                                            Ajouter
                                        </Button>
                                    )}
                                </Box>
                                {client.Utilisateurs?.length > 0 ? (
                                    <List dense disablePadding>
                                        {client.Utilisateurs.map((u, i) => (
                                            <React.Fragment key={u.id_utilisateur ?? i}>
                                                {i > 0 && <Divider component="li" />}
                                                <ListItem sx={{ px: 0 }}
                                                    secondaryAction={
                                                        <>
                                                            {canConnectorAccess && (
                                                                <IconButton edge="end" size="small" sx={{ color: 'primary.main' }}
                                                                    onClick={() => startConnectorAccess(u)}
                                                                    title="Accéder à Connector avec l'accord de cet utilisateur">
                                                                    <VpnKeyIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            {canStartSupport && (
                                                                <IconButton edge="end" size="small" sx={{ color: 'primary.main' }}
                                                                    onClick={() => startSupportSession(u)}
                                                                    title="Activer le support (session live avec preuve d'accès)">
                                                                    <SupportAgentIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            {canManageUsers && (
                                                                <IconButton edge="end" size="small" sx={{ color: 'error.main' }}
                                                                    onClick={() => handleDeleteUser(u.id_utilisateur)}
                                                                    title="Supprimer l'utilisateur">
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </>
                                                    }
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem' }}>
                                                            {(u.prenom?.[0] || u.nom?.[0] || '?').toUpperCase()}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={`${u.prenom || ''} ${u.nom || ''}`.trim() || '—'}
                                                        secondary={
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                                {u.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <EmailIcon sx={{ fontSize: 12 }} /><Typography variant="caption">{u.email}</Typography>
                                                                </Box>}
                                                                {u.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <PhoneIcon sx={{ fontSize: 12 }} /><Typography variant="caption">{u.phone}</Typography>
                                                                </Box>}
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Aucun utilisateur associé</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right column */}
                    <Grid item xs={12} lg={4}>
                        <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>Licences actives</Typography>
                                {subscriptions.filter(isSubActive).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Aucune licence active</Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {subscriptions.filter(isSubActive).map((ca, i) => (
                                            <ListItem key={i} sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={getAppName(ca.key_application)}
                                                    secondary={
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            <Typography variant="caption">{formatMontant(ca)}</Typography>
                                                            {ca.date_fin && <Typography variant="caption" color="text.secondary">
                                                                · expire le {new Date(ca.date_fin).toLocaleDateString('fr-FR')}
                                                            </Typography>}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ── Tab 1 : Licences ──────────────────────────────────────────── */}
            {tabValue === 1 && (
                <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Licences ({subscriptions.length})
                            </Typography>
                            {canManageLicenses && (
                                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAddSubDialog}
                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                                    Ajouter
                                </Button>
                            )}
                        </Box>

                        {/* Quotas du cycle en cours : accordé (formules + recharges), consommé, reste */}
                        {Object.entries(quotas).flatMap(([keyApplication, quota]) =>
                            (quota.quotas || []).filter(q => q.quantity_limit != null).map(q => {
                                const limit = parseFloat(q.quantity_limit);
                                const used = parseFloat(q.used) || 0;
                                const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
                                return (
                                    <Paper key={`${keyApplication}-${q.dimension}`} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography variant="subtitle2">
                                                    Quota {getAppName(keyApplication)} — {q.dimension || 'sans unité'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {used.toLocaleString('fr-FR')} consommé(s) sur {limit.toLocaleString('fr-FR')}
                                                    {parseFloat(q.topup_quantity) > 0 && ` (dont ${parseFloat(q.topup_quantity).toLocaleString('fr-FR')} en recharge)`}
                                                    {' · reste '}{(parseFloat(q.remaining) || 0).toLocaleString('fr-FR')}
                                                    {' · prochaine recharge le '}{new Date(quota.period_end).toLocaleDateString('fr-FR')}
                                                </Typography>
                                            </Box>
                                            {canManageLicenses && q.dimension && (
                                                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                                                    onClick={() => openTopupDialog(keyApplication, q.dimension)}>
                                                    Recharger
                                                </Button>
                                            )}
                                        </Box>
                                        <LinearProgress variant="determinate" value={percent}
                                            color={percent >= 100 ? 'error' : percent > 80 ? 'warning' : 'primary'}
                                            sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                                    </Paper>
                                );
                            })
                        )}

                        {subscriptions.length === 0 ? (
                            <Alert severity="info">Aucune licence pour ce client.</Alert>
                        ) : (
                            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 800 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Application</TableCell>
                                            <TableCell>Début</TableCell>
                                            <TableCell>Expiration</TableCell>
                                            <TableCell>Licence</TableCell>
                                            <TableCell>Statut</TableCell>
                                            <TableCell align="center">Factures</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscriptions.map((ca) => (
                                            <React.Fragment key={ca.id}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {getAppName(ca.key_application)}
                                                        {ca.application_url && (
                                                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                                URL: {ca.application_url}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {ca.date_debut ? new Date(ca.date_debut).toLocaleDateString('fr-FR') : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {ca.date_fin ? new Date(ca.date_fin).toLocaleDateString('fr-FR') : 'Sans expiration'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Chip
                                                                label={LICENCE_TYPES.find(l => l.value === ca.licence_type)?.label || ca.licence_type || '—'}
                                                                size="small" variant="outlined"
                                                                color={ca.licence_type === 'gratuite' ? 'success' : ca.licence_type === 'essai' ? 'warning' : 'primary'}
                                                                sx={{ mb: 0.5, mr: 0.5 }}
                                                            />
                                                            {ca.is_beta_tester && (
                                                                <Chip icon={<ScienceIcon />} label="Bêta testeur" size="small"
                                                                    variant="outlined" color="warning" sx={{ mb: 0.5 }} />
                                                            )}
                                                            {ca.licence_type !== 'gratuite' && ca.montant != null && (
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    {parseFloat(ca.montant).toFixed(2)} €
                                                                    {ca.recurrence ? ` / ${ca.recurrence}` : ''}
                                                                </Typography>
                                                            )}
                                                            {ca.quantity_limit != null && (
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    {ca.licence_type === 'recharge' ? 'Recharge' : 'Quota'} : {parseFloat(ca.quantity_limit).toLocaleString('fr-FR')} {ca.dimension || ''}
                                                                    {ca.licence_type !== 'recharge' && ' / mois'}
                                                                    {ca.licence_type !== 'recharge' && ` — ${ca.pricing_plan_name || 'personnalisé'}`}
                                                                </Typography>
                                                            )}
                                                            {ca.authorizations?.length > 0 && (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                                    {ca.authorizations.map(a => (
                                                                        <Chip key={a} label={a} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={isSubActive(ca) ? 'Actif' : 'Expiré'}
                                                            color={isSubActive(ca) ? 'success' : 'error'} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title={expandedSub === ca.key_application ? 'Masquer les factures' : 'Voir les factures'}>
                                                            <IconButton size="small" onClick={() => toggleExpandSub(ca.key_application)}
                                                                sx={{ color: 'primary.main' }}>
                                                                <ReceiptIcon fontSize="small" />
                                                                {expandedSub === ca.key_application ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={expandedOptionsFor === ca.key_application ? 'Masquer les options' : 'Voir les options'}>
                                                            <IconButton size="small" onClick={() => toggleExpandOptions(ca.key_application)}
                                                                sx={{ color: 'primary.main' }}>
                                                                <TuneIcon fontSize="small" />
                                                                {expandedOptionsFor === ca.key_application ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {canManageLicenses && (
                                                            <>
                                                                <IconButton size="small" onClick={() => openEditSubDialog(ca)} sx={{ color: 'primary.main', mr: 0.5 }} title="Modifier">
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleDeleteSub(ca)} sx={{ color: 'error.main' }} title="Supprimer">
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                </TableRow>

                                                {/* ── Factures expandées ── */}
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedSub === ca.key_application ? undefined : 'none' }}>
                                                        <Collapse in={expandedSub === ca.key_application} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                        Factures — {getAppName(ca.key_application)}
                                                                    </Typography>
                                                                    <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
                                                                        onClick={() => openUploadDialog(ca.key_application)}
                                                                        sx={{ borderColor: 'primary.main', color: 'primary.main' }}>
                                                                        Déposer une facture
                                                                    </Button>
                                                                </Box>

                                                                {facturesLoading[ca.key_application] ? (
                                                                    <CircularProgress size={20} />
                                                                ) : !factures[ca.key_application] || factures[ca.key_application].length === 0 ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Aucune facture pour cette licence.
                                                                    </Typography>
                                                                ) : (
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Fichier</TableCell>
                                                                                <TableCell>Date facture</TableCell>
                                                                                <TableCell>Montant</TableCell>
                                                                                <TableCell>Statut</TableCell>
                                                                                <TableCell align="right">Actions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {factures[ca.key_application].map(f => (
                                                                                <TableRow key={f.id}>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{f.nom_original}</TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                        {f.date_facture ? new Date(f.date_facture).toLocaleDateString('fr-FR') : '—'}
                                                                                    </TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                        {f.montant != null ? `${parseFloat(f.montant).toFixed(2)} €` : '—'}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Chip label={STATUTS_FACTURE.find(s => s.value === f.statut)?.label || f.statut}
                                                                                            size="small" color={STATUT_COLORS[f.statut] || 'default'} variant="outlined" />
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        <Tooltip title="Télécharger">
                                                                                            <IconButton size="small" onClick={() => handleDownloadFacture(f.id, f.nom_original)} sx={{ color: 'primary.main', mr: 0.5 }}>
                                                                                                <DownloadIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                        <Tooltip title="Supprimer">
                                                                                            <IconButton size="small" onClick={() => handleDeleteFacture(ca.key_application, f.id)} sx={{ color: 'error.main' }}>
                                                                                                <DeleteIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>

                                                {/* ── Options expandées ── */}
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedOptionsFor === ca.key_application ? undefined : 'none' }}>
                                                        <Collapse in={expandedOptionsFor === ca.key_application} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                        Options — {getAppName(ca.key_application)}
                                                                    </Typography>
                                                                    {canManageLicenses && (
                                                                        <Button size="small" variant="outlined" startIcon={<AddIcon />}
                                                                            onClick={() => openAddOptionLicense(ca.key_application)}
                                                                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}>
                                                                            Ajouter une option
                                                                        </Button>
                                                                    )}
                                                                </Box>

                                                                {subOptionsLoading[ca.key_application] ? (
                                                                    <CircularProgress size={20} />
                                                                ) : !subOptions[ca.key_application] || subOptions[ca.key_application].length === 0 ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Aucune option licenciée pour cette application.
                                                                    </Typography>
                                                                ) : (
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Option</TableCell>
                                                                                <TableCell>Expiration</TableCell>
                                                                                <TableCell>Licence</TableCell>
                                                                                <TableCell>Quota</TableCell>
                                                                                <TableCell>Droits</TableCell>
                                                                                <TableCell align="right">Actions</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {subOptions[ca.key_application].map(opt => (
                                                                                <TableRow key={opt.id}>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                        {getOptionName(ca.key_application, opt.application_option_id)}
                                                                                    </TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                        {opt.date_fin ? new Date(opt.date_fin).toLocaleDateString('fr-FR') : 'Sans expiration'}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Chip
                                                                                            label={LICENCE_TYPES.find(l => l.value === opt.licence_type)?.label || opt.licence_type || '—'}
                                                                                            size="small" variant="outlined"
                                                                                            color={opt.licence_type === 'gratuite' ? 'success' : opt.licence_type === 'essai' ? 'warning' : 'primary'}
                                                                                        />
                                                                                        {opt.is_beta_tester && (
                                                                                            <Chip icon={<ScienceIcon />} label="Bêta" size="small"
                                                                                                variant="outlined" color="warning" sx={{ ml: 0.5 }} />
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                                        {opt.quantity_limit != null ? `${parseFloat(opt.quantity_limit)} ${opt.dimension || ''}` : '—'}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {opt.authorizations?.length > 0 ? (
                                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                                {opt.authorizations.map(a => (
                                                                                                    <Chip key={a} label={a} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                                                                ))}
                                                                                            </Box>
                                                                                        ) : '—'}
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        {canManageLicenses && (
                                                                                            <>
                                                                                                <IconButton size="small" onClick={() => openEditOptionLicense(ca.key_application, opt)} sx={{ color: 'primary.main', mr: 0.5 }} title="Modifier">
                                                                                                    <EditIcon fontSize="small" />
                                                                                                </IconButton>
                                                                                                <IconButton size="small" onClick={() => handleDeleteOptionLicense(ca.key_application, opt.application_option_id)} sx={{ color: 'error.main' }} title="Supprimer">
                                                                                                    <DeleteIcon fontSize="small" />
                                                                                                </IconButton>
                                                                                            </>
                                                                                        )}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            )}


            {/* ── Subscription Dialog ───────────────────────────────────────── */}
            <Dialog open={openSubDialog} onClose={() => setOpenSubDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'primary.main' }}>
                    {editingSub ? 'Modifier la licence' : 'Ajouter une licence'}
                </DialogTitle>
                <DialogContent>
                    {subError && <Alert severity="error" sx={{ mb: 2 }}>{subError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {/* Application */}
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={!!editingSub}>
                                <InputLabel>Application *</InputLabel>
                                <Select value={subForm.key_application}
                                    onChange={(e) => { setSubForm(p => ({ ...p, key_application: e.target.value, pricing_plan_id: '' })); loadPermissionsCatalog(e.target.value); loadPacksCatalog(e.target.value); }} label="Application *">
                                    {applications.map(app => (
                                        <MenuItem key={app.client_id} value={app.client_id}>{app.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Type de licence */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type de licence</InputLabel>
                                <Select value={subForm.licence_type}
                                    onChange={(e) => setSubForm(p => ({ ...p, licence_type: e.target.value }))} label="Type de licence">
                                    {LICENCE_TYPES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Montant */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Montant"
                                type="number" inputProps={{ min: 0, step: 0.01 }}
                                value={subForm.montant}
                                onChange={(e) => setSubForm(p => ({ ...p, montant: e.target.value }))}
                                disabled={subForm.licence_type === 'gratuite'}
                                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                            />
                        </Grid>

                        {/* Récurrence */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={subForm.licence_type === 'gratuite'}>
                                <InputLabel>Récurrence</InputLabel>
                                <Select value={subForm.recurrence}
                                    onChange={(e) => handleRecurrenceChange(e.target.value)} label="Récurrence">
                                    {RECURRENCES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Date début */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Date de début" type="date"
                                value={subForm.date_debut}
                                onChange={(e) => setSubForm(p => ({ ...p, date_debut: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>

                        {/* Date fin */}
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Date d'expiration" type="date"
                                value={subForm.date_fin}
                                onChange={(e) => setSubForm(p => ({ ...p, date_fin: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                helperText="Laisser vide = sans expiration" />
                        </Grid>

                        {/* Pack de l'application (quota mensuel prédéfini) ou quantité personnalisée */}
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={!subForm.key_application}>
                                <InputLabel shrink>Pack</InputLabel>
                                <Select displayEmpty notched value={subForm.pricing_plan_id}
                                    onChange={(e) => handleSelectPack(e.target.value)} label="Pack">
                                    <MenuItem value="">Quantité personnalisée</MenuItem>
                                    {(packsCatalog[subForm.key_application] || []).map(pack => (
                                        <MenuItem key={pack.id} value={String(pack.id)}>
                                            {pack.name} — {parseFloat(pack.quantity_limit).toLocaleString('fr-FR')} {pack.dimension || ''} / mois
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    Les packs se créent dans la fiche tarification de l'application (quota mensuel inclus).
                                    Changer de pack en cours de mois applique le nouveau quota tout de suite.
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* Quota d'usage : celui du pack, ou saisi librement */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Quantité max par mois (optionnel)"
                                type="number" inputProps={{ min: 0, step: 1 }}
                                value={subForm.quantity_limit}
                                onChange={(e) => setSubForm(p => ({ ...p, quantity_limit: e.target.value }))}
                                helperText="Rechargée à chaque date anniversaire de la licence. Vide = sans limite" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Unité"
                                value={subForm.dimension}
                                onChange={(e) => setSubForm(p => ({ ...p, dimension: e.target.value }))}
                                placeholder="ex: transfers"
                                helperText="Celle que l'application consomme (CONNECTOR : transfers)" />
                        </Grid>

                        {/* Droits accordés par cette licence */}
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple size="small"
                                options={(permissionsCatalog[subForm.key_application] || []).map(p => p.key)}
                                getOptionLabel={(key) => {
                                    const found = (permissionsCatalog[subForm.key_application] || []).find(p => p.key === key);
                                    return found ? `${found.name} (${found.key})` : key;
                                }}
                                value={subForm.authorizations}
                                onChange={(e, newValue) => setSubForm(p => ({ ...p, authorizations: newValue }))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const found = (permissionsCatalog[subForm.key_application] || []).find(p => p.key === option);
                                        return <Chip label={found ? found.name : option} size="small" {...getTagProps({ index })} key={option} />;
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Droits accordés (authorizations)"
                                        placeholder={(permissionsCatalog[subForm.key_application] || []).length ? undefined : 'Aucune permission définie pour cette application'}
                                        helperText="Se cumulent avec les authorizations des autres licences actives de ce client sur cette application. Catalogue géré dans l'onglet Permissions de la fiche tarification de l'application." />
                                )}
                            />
                        </Grid>

                        {/* URL personnalisée pour ce client */}
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>URL personnalisée (optionnel)</InputLabel>
                                <Select value={subForm.application_url}
                                    onChange={(e) => setSubForm(p => ({ ...p, application_url: e.target.value }))}
                                    label="URL personnalisée (optionnel)">
                                    <MenuItem value=""><em>Aucune — utiliser l'URL par défaut de l'application</em></MenuItem>
                                    {(applications.find(a => a.client_id === subForm.key_application)?.redirect_urls || [])
                                        .map(url => <MenuItem key={url} value={url}>{url}</MenuItem>)}
                                </Select>
                                <FormHelperText>
                                    Choisie parmi les redirect_urls déclarées pour cette application. Remplace l'URL par défaut pour ce client uniquement.
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* Bêta testeur */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={subForm.is_beta_tester}
                                    onChange={(e) => setSubForm(p => ({ ...p, is_beta_tester: e.target.checked }))} />}
                                label="Bêta testeur (accès anticipé, tarif éventuellement dérogatoire via le montant ci-dessus)"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSubDialog(false)}>Annuler</Button>
                    <Button onClick={handleSaveSub} variant="contained" disabled={subSaving || !subForm.key_application}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {subSaving ? <CircularProgress size={20} color="inherit" /> : editingSub ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Recharge ponctuelle de quota ─────────────────────────────────── */}
            <Dialog open={!!topupTarget} onClose={() => setTopupTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'primary.main' }}>
                    Recharger le quota{topupTarget && ` — ${getAppName(topupTarget.key_application)}`}
                </DialogTitle>
                <DialogContent>
                    {topupError && <Alert severity="error" sx={{ mb: 2 }}>{topupError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Quantité supplémentaire *"
                                type="number" inputProps={{ min: 1, step: 1 }}
                                value={topupForm.quantity}
                                onChange={(e) => setTopupForm(p => ({ ...p, quantity: e.target.value }))}
                                InputProps={{ endAdornment: <InputAdornment position="end">{topupTarget?.dimension}</InputAdornment> }}
                                helperText={topupTarget && quotas[topupTarget.key_application]
                                    ? `S'ajoute au quota jusqu'au ${new Date(quotas[topupTarget.key_application].period_end).toLocaleDateString('fr-FR')} (fin du cycle en cours), puis expire.`
                                    : ''} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Montant (optionnel)"
                                type="number" inputProps={{ min: 0, step: 0.01 }}
                                value={topupForm.montant}
                                onChange={(e) => setTopupForm(p => ({ ...p, montant: e.target.value }))}
                                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTopupTarget(null)}>Annuler</Button>
                    <Button onClick={handleSaveTopup} variant="contained"
                        disabled={topupSaving || !(parseFloat(topupForm.quantity) > 0)}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {topupSaving ? <CircularProgress size={20} color="inherit" /> : 'Recharger'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Option License Dialog ────────────────────────────────────────── */}
            <Dialog open={openOptionLicenseDialog} onClose={() => setOpenOptionLicenseDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'primary.main' }}>
                    {editingOptionLicense ? "Modifier la licence d'option" : 'Ajouter une licence d\'option'}
                    {optionLicenseForApp && ` — ${getAppName(optionLicenseForApp)}`}
                </DialogTitle>
                <DialogContent>
                    {optionLicenseError && <Alert severity="error" sx={{ mb: 2 }}>{optionLicenseError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={!!editingOptionLicense}>
                                <InputLabel>Option *</InputLabel>
                                <Select value={optionLicenseForm.application_option_id}
                                    onChange={(e) => setOptionLicenseForm(p => ({ ...p, application_option_id: e.target.value }))} label="Option *">
                                    {(optionsCatalog[optionLicenseForApp] || []).map(opt => (
                                        <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type de licence</InputLabel>
                                <Select value={optionLicenseForm.licence_type}
                                    onChange={(e) => setOptionLicenseForm(p => ({ ...p, licence_type: e.target.value }))} label="Type de licence">
                                    {LICENCE_TYPES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Montant"
                                type="number" inputProps={{ min: 0, step: 0.01 }}
                                value={optionLicenseForm.montant}
                                onChange={(e) => setOptionLicenseForm(p => ({ ...p, montant: e.target.value }))}
                                disabled={optionLicenseForm.licence_type === 'gratuite'}
                                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={optionLicenseForm.licence_type === 'gratuite'}>
                                <InputLabel>Récurrence</InputLabel>
                                <Select value={optionLicenseForm.recurrence}
                                    onChange={(e) => setOptionLicenseForm(p => ({ ...p, recurrence: e.target.value }))} label="Récurrence">
                                    {RECURRENCES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Date de début" type="date"
                                value={optionLicenseForm.date_debut}
                                onChange={(e) => setOptionLicenseForm(p => ({ ...p, date_debut: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Date d'expiration" type="date"
                                value={optionLicenseForm.date_fin}
                                onChange={(e) => setOptionLicenseForm(p => ({ ...p, date_fin: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                helperText="Laisser vide = sans expiration" />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Quantité max (optionnel)"
                                type="number" inputProps={{ min: 0, step: 0.01 }}
                                value={optionLicenseForm.quantity_limit}
                                onChange={(e) => setOptionLicenseForm(p => ({ ...p, quantity_limit: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Unité"
                                value={optionLicenseForm.dimension}
                                onChange={(e) => setOptionLicenseForm(p => ({ ...p, dimension: e.target.value }))}
                                placeholder="ex: transfer_gb" />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                multiple size="small"
                                options={(permissionsCatalog[optionLicenseForApp] || []).map(p => p.key)}
                                getOptionLabel={(key) => {
                                    const found = (permissionsCatalog[optionLicenseForApp] || []).find(p => p.key === key);
                                    return found ? `${found.name} (${found.key})` : key;
                                }}
                                value={optionLicenseForm.authorizations}
                                onChange={(e, newValue) => setOptionLicenseForm(p => ({ ...p, authorizations: newValue }))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const found = (permissionsCatalog[optionLicenseForApp] || []).find(p => p.key === option);
                                        return <Chip label={found ? found.name : option} size="small" {...getTagProps({ index })} key={option} />;
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Droits accordés (authorizations)"
                                        placeholder={(permissionsCatalog[optionLicenseForApp] || []).length ? undefined : 'Aucune permission définie pour cette application'}
                                        helperText="Se cumulent avec les authorizations des autres licences actives de ce client sur cette application. Catalogue géré dans l'onglet Permissions de la fiche tarification de l'application." />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={optionLicenseForm.is_beta_tester}
                                    onChange={(e) => setOptionLicenseForm(p => ({ ...p, is_beta_tester: e.target.checked }))} />}
                                label="Bêta testeur"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenOptionLicenseDialog(false)}>Annuler</Button>
                    <Button onClick={handleSaveOptionLicense} variant="contained"
                        disabled={optionLicenseSaving || !optionLicenseForm.application_option_id}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {optionLicenseSaving ? <CircularProgress size={20} color="inherit" /> : editingOptionLicense ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── User Dialog ───────────────────────────────────────────────── */}
            <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAddIcon /> Ajouter un utilisateur
                </DialogTitle>
                <DialogContent>
                    {userError && <Alert severity="error" sx={{ mb: 2 }}>{userError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Prénom *"
                                value={userForm.prenom}
                                onChange={(e) => setUserForm(p => ({ ...p, prenom: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Nom *"
                                value={userForm.nom}
                                onChange={(e) => setUserForm(p => ({ ...p, nom: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Email *" type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))} />
                        </Grid>

                        {/* Choix du mode mot de passe */}
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Mot de passe
                            </Typography>
                            <ToggleButtonGroup
                                value={userPasswordMode}
                                exclusive
                                onChange={(e, val) => { if (val) { setUserPasswordMode(val); setUserForm(p => ({ ...p, password: '' })); } }}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="temp" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                                    Envoyer par email (temporaire)
                                </ToggleButton>
                                <ToggleButton value="manual" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                                    Définir manuellement
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        {userPasswordMode === 'temp' && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    Un mot de passe temporaire sera généré et envoyé à l'utilisateur par email.
                                </Alert>
                            </Grid>
                        )}

                        {userPasswordMode === 'manual' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth size="small" label="Mot de passe *"
                                    type={userShowPassword ? 'text' : 'password'}
                                    value={userForm.password}
                                    onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setUserShowPassword(v => !v)}>
                                                    {userShowPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUserDialog(false)}>Annuler</Button>
                    <Button onClick={handleAddUser} variant="contained"
                        disabled={userSaving || !userForm.nom || !userForm.prenom || !userForm.email || (userPasswordMode === 'manual' && !userForm.password)}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {userSaving ? <CircularProgress size={20} color="inherit" /> : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Facture Upload Dialog ─────────────────────────────────────── */}
            <Dialog open={openFactureDialog} onClose={() => setOpenFactureDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'primary.main' }}>Déposer une facture</DialogTitle>
                <DialogContent>
                    {factureError && <Alert severity="error" sx={{ mb: 2 }}>{factureError}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {/* Fichier */}
                        <Grid item xs={12}>
                            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
                                onChange={(e) => setFactureFile(e.target.files[0] || null)} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button variant="outlined" startIcon={<UploadFileIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderColor: 'primary.main', color: 'primary.main' }}>
                                    Choisir un fichier
                                </Button>
                                <Typography variant="body2" color={factureFile ? 'text.primary' : 'text.secondary'} noWrap>
                                    {factureFile ? factureFile.name : 'Aucun fichier sélectionné'}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">PDF, JPG, PNG — 20 Mo max</Typography>
                        </Grid>

                        {/* Date facture */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Date de la facture" type="date"
                                value={factureForm.date_facture}
                                onChange={(e) => setFactureForm(p => ({ ...p, date_facture: e.target.value }))}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>

                        {/* Montant facture */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Montant de la facture"
                                type="number" inputProps={{ min: 0, step: 0.01 }}
                                value={factureForm.montant}
                                onChange={(e) => setFactureForm(p => ({ ...p, montant: e.target.value }))}
                                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                            />
                        </Grid>

                        {/* Statut */}
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Statut</InputLabel>
                                <Select value={factureForm.statut}
                                    onChange={(e) => setFactureForm(p => ({ ...p, statut: e.target.value }))} label="Statut">
                                    {STATUTS_FACTURE.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenFactureDialog(false)}>Annuler</Button>
                    <Button onClick={handleUploadFacture} variant="contained" disabled={factureUploading || !factureFile}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {factureUploading ? <CircularProgress size={20} color="inherit" /> : 'Déposer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog accès Connector avec consentement */}
            <Dialog open={!!connectorTargetUser} onClose={closeConnectorDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Accès Connector — {connectorTargetUser?.prenom} {connectorTargetUser?.nom}
                </DialogTitle>
                <DialogContent>
                    {connectorStep === 'waiting' && !connectorError && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary" align="center">
                                En attente de la réponse de {connectorTargetUser?.prenom} dans Connector...
                            </Typography>
                        </Box>
                    )}
                    {connectorStep === 'code_entry' && (
                        <Box sx={{ pt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                {connectorTargetUser?.prenom} a autorisé l'accès. Demandez-lui le code à 5 caractères
                                affiché dans Connector, puis saisissez-le ci-dessous :
                            </Typography>
                            {connectorError && <Alert severity="error" sx={{ mb: 2 }}>{connectorError}</Alert>}
                            <TextField
                                fullWidth autoFocus label="Code" value={connectorCode}
                                onChange={e => setConnectorCode(e.target.value.toUpperCase())}
                                inputProps={{ maxLength: 5, style: { letterSpacing: 4, fontFamily: 'monospace', textAlign: 'center' } }}
                            />
                        </Box>
                    )}
                    {connectorStep === 'active' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2 }}>
                            <Alert severity="success" sx={{ width: '100%' }}>
                                Accès accordé. Il reste actif tant que votre session Connector est ouverte.
                            </Alert>
                            <Button variant="contained" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                                onClick={() => window.open(process.env.REACT_APP_CONNECTOR_URL || 'http://localhost:3000', '_blank', 'noopener,noreferrer')}>
                                Ouvrir Connector
                            </Button>
                        </Box>
                    )}
                    {connectorStep === 'error' && (
                        <Alert severity="error">{connectorError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConnectorDialog}>{connectorStep === 'active' ? 'Fermer' : 'Annuler'}</Button>
                    {connectorStep === 'code_entry' && (
                        <Button onClick={submitConnectorCode} variant="contained" disabled={connectorSubmitting || connectorCode.trim().length !== 5}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                            {connectorSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Valider'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog session de support live (OTP + preuve rrweb) */}
            <Dialog open={!!supportTargetUser} onClose={closeSupportDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    Support live — {supportTargetUser?.prenom} {supportTargetUser?.nom}
                </DialogTitle>
                <DialogContent>
                    {supportStep === 'starting' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary" align="center">
                                Démarrage de la session...
                            </Typography>
                        </Box>
                    )}
                    {supportStep === 'needs_link' && (
                        <Box sx={{ pt: 1 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Ce client n'est pas encore lié à un compte QR Code. Renseignez son account_key
                                (visible dans qrcode_server) pour activer le support une première fois.
                            </Alert>
                            {supportError && <Alert severity="error" sx={{ mb: 2 }}>{supportError}</Alert>}
                            <TextField
                                fullWidth autoFocus label="account_key" value={supportAccountKeyInput}
                                onChange={e => setSupportAccountKeyInput(e.target.value.trim())}
                            />
                        </Box>
                    )}
                    {supportStep === 'otp_entry' && (
                        <Box sx={{ pt: 1 }}>
                            {!supportClientOnline && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Aucune session cliente détectée en ligne actuellement. Le code sera délivré dès
                                    que le client ouvrira l'application (tant que le délai n'est pas expiré).
                                </Alert>
                            )}
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Demandez au client le code à 6 chiffres affiché dans l'application, puis
                                saisissez-le ci-dessous pour autoriser l'accès :
                            </Typography>
                            {supportError && <Alert severity="error" sx={{ mb: 2 }}>{supportError}</Alert>}
                            <TextField
                                fullWidth autoFocus label="Code" value={supportOtp}
                                onChange={e => setSupportOtp(e.target.value.replace(/\D/g, ''))}
                                inputProps={{ maxLength: 6, style: { letterSpacing: 4, fontFamily: 'monospace', textAlign: 'center' } }}
                            />
                        </Box>
                    )}
                    {supportStep === 'active' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2 }}>
                            <Alert severity="success" sx={{ width: '100%' }}>
                                Accès autorisé. La session est enregistrée comme preuve d'accès.
                            </Alert>
                            <Button variant="contained" startIcon={<SupportAgentIcon />}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                                onClick={openSupportViewer}>
                                Ouvrir la session
                            </Button>
                        </Box>
                    )}
                    {supportStep === 'error' && (
                        <Alert severity="error">{supportError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSupportDialog}>{supportStep === 'active' ? 'Fermer' : 'Annuler'}</Button>
                    {supportStep === 'needs_link' && (
                        <Button onClick={submitSupportLink} variant="contained" disabled={supportSubmitting || !supportAccountKeyInput.trim()}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                            {supportSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Lier et continuer'}
                        </Button>
                    )}
                    {supportStep === 'otp_entry' && (
                        <Button onClick={submitSupportOtp} variant="contained" disabled={supportSubmitting || supportOtp.trim().length !== 6}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                            {supportSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Valider'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
